import { useState, useEffect } from "react";
import { Phone, Users as UsersIcon, UserPlus, Plus, Crown, Shield, User, Trash2, AlertTriangle, Copy, Share2, Check, ExternalLink, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { PhoneNumberInput } from "@/components/ui/phone-input";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { invitationApi, accountMembersApi, InvitationResponseDto, AccountMemberDto } from "@/lib/api";
import { useUser } from "@/contexts/UserContext";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function Users() {
  const { t, i18n } = useTranslation();
  const { user: currentUser } = useUser();
  const isRTL = i18n.language === 'he';
  const [members, setMembers] = useState<AccountMemberDto[]>([]);
  const [invitations, setInvitations] = useState<InvitationResponseDto[]>([]);
  const [invitePhone, setInvitePhone] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteSuccessDialogOpen, setInviteSuccessDialogOpen] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<AccountMemberDto | null>(null);
  const [removeLoading, setRemoveLoading] = useState(false);

  // Website URL for sharing
  const websiteUrl = window.location.origin;

  // Check if current logged-in user is owner (can remove members)
  const currentUserMember = members.find(m => m.userId === currentUser?.uid);
  const currentUserIsOwner = currentUserMember?.role === 'owner';

  // Format phone number to +972-54-7760-683 style
  const formatPhoneNumber = (phoneNumber: string | null) => {
    if (!phoneNumber) return '';
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    if (cleaned.startsWith('972')) {
      const countryCode = cleaned.slice(0, 3);
      const part1 = cleaned.slice(3, 5);
      const part2 = cleaned.slice(5, 9);
      const part3 = cleaned.slice(9);
      return `+${countryCode}-${part1}-${part2}-${part3}`;
    } else if (cleaned.startsWith('0')) {
      const part1 = cleaned.slice(1, 3);
      const part2 = cleaned.slice(3, 7);
      const part3 = cleaned.slice(7);
      return `+972-${part1}-${part2}-${part3}`;
    }
    
    return phoneNumber;
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-amber-500" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-500" />;
      default:
        return <User className="h-4 w-4 text-gray-500" />;
    }
  };

  const getRoleBadgeVariant = (role: string): "default" | "secondary" | "outline" => {
    switch (role) {
      case 'owner':
        return 'default';
      case 'admin':
        return 'secondary';
      default:
        return 'outline';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner':
        return t('users.owner');
      case 'admin':
        return t('users.admin');
      default:
        return t('users.member');
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [membersResponse, invitationsResponse] = await Promise.all([
        accountMembersApi.getMembers(),
        invitationApi.getAllInvitations(),
      ]);
      setMembers(membersResponse.members);
      // Only show pending invitations (accepted ones are now shown as members)
      setInvitations(invitationsResponse.filter(inv => inv.status === 'pending'));
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error(t('users.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async () => {
    if (!invitePhone.trim()) {
      toast.error(t('invite.phoneNumberRequired'));
      return;
    }

    const phoneRegex = /^\+\d{10,15}$/;
    if (!phoneRegex.test(invitePhone.replace(/[\s-]/g, ''))) {
      toast.error(t('invite.invalidPhoneNumber'));
      return;
    }

    setInviteLoading(true);
    try {
      await invitationApi.createInvitation({ phoneNumber: invitePhone });
      setInvitePhone("");
      setInviteDialogOpen(false);
      setInviteSuccessDialogOpen(true); // Show success dialog with instructions
      loadData();
    } catch (error: any) {
      console.error('Failed to invite user:', error);
      if (error.status === 409) {
        toast.error(t('invite.alreadyExists'));
      } else {
        toast.error(t('invite.inviteFailed'));
      }
    } finally {
      setInviteLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(websiteUrl);
      setCopiedLink(true);
      toast.success(t('invite.linkCopied'));
      setTimeout(() => setCopiedLink(false), 2000);
    } catch (error) {
      toast.error(t('invite.copyFailed'));
    }
  };

  const handleShareLink = async () => {
    const shareData = {
      title: t('invite.shareTitle'),
      text: t('invite.shareText'),
      url: websiteUrl,
    };

    if (navigator.share && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled or share failed, fall back to copy
        handleCopyLink();
      }
    } else {
      // Fallback to copy if Web Share API not supported
      handleCopyLink();
    }
  };

  const handleShareWhatsApp = () => {
    const message = `${t('invite.shareText')}\n${websiteUrl}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await invitationApi.deleteInvitation(invitationId);
      toast.success(t('invite.cancelled'));
      loadData();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      toast.error(t('invite.cancelFailed'));
    }
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;

    setRemoveLoading(true);
    try {
      await accountMembersApi.removeMember(memberToRemove.userId);
      toast.success(t('users.memberRemoved'));
      setMemberToRemove(null);
      loadData();
    } catch (error: any) {
      console.error('Failed to remove member:', error);
      if (error.status === 409) {
        toast.error(t('users.cannotRemoveOwner'));
      } else {
        toast.error(t('users.removeFailed'));
      }
    } finally {
      setRemoveLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const getInitials = (name: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="container max-w-4xl mx-auto p-6 page-shell" dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }}>
      <div className={`page-heading ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold">{t('users.title')}</h1>
          <p className="text-muted-foreground">
            {t('users.subtitle')}
          </p>
        </div>
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <Plus className="h-4 w-4" />
              {t('invite.inviteUser')}
            </Button>
          </DialogTrigger>
          <DialogContent dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }}>
            <DialogHeader>
              <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <UserPlus className="h-5 w-5" />
                {t('invite.title')}
              </DialogTitle>
              <DialogDescription>
                {t('invite.subtitle')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>{t('invite.phoneNumber')}</Label>
                <PhoneNumberInput
                  value={invitePhone}
                  onChange={(value) => setInvitePhone(value || '')}
                  placeholder={t('invite.enterPhoneNumber')}
                />
              </div>
            </div>
            <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
              <Button 
                variant="outline" 
                onClick={() => {
                  setInviteDialogOpen(false);
                  setInvitePhone("");
                }}
              >
                {t('invite.cancel')}
              </Button>
              <Button 
                onClick={handleInviteUser} 
                disabled={inviteLoading || !invitePhone.trim()}
                className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
              >
                {inviteLoading ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
                ) : (
                  <UserPlus className="h-4 w-4" />
                )}
                {t('invite.sendInvite')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Account Members */}
      <Card dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }} className="border-border/90">
        <CardHeader>
          <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
            <UsersIcon className="h-5 w-5" />
            {t('users.accountMembers')}
          </CardTitle>
          <CardDescription>
            {t('users.accountMembersDesc')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : members.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('users.noMembers')}
            </div>
          ) : (
            <div className="space-y-3">
              {members.map((member) => (
                <div 
                  key={member.userId} 
                  className={`flex items-center justify-between p-4 border rounded-lg ${
                    member.role === 'owner' ? 'bg-amber-500/12 border-amber-500/30' : 'hover:bg-accent/50 border-border/80'
                  } transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className={member.role === 'owner' ? 'bg-amber-100 text-amber-700' : ''}>
                        {getInitials(member.fullName)}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        <p className="font-medium">{member.fullName || t('users.unnamed')}</p>
                        {getRoleIcon(member.role)}
                      </div>
                      {member.phoneNumber && (
                        <p className="text-sm text-muted-foreground" dir="ltr">
                          {formatPhoneNumber(member.phoneNumber)}
                        </p>
                      )}
                      {member.email && (
                        <p className="text-sm text-muted-foreground">{member.email}</p>
                      )}
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Badge variant={getRoleBadgeVariant(member.role)}>
                      {getRoleLabel(member.role)}
                    </Badge>
                    {/* Show remove button for non-owners (only owner can remove) */}
                    {member.role !== 'owner' && currentUserIsOwner && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setMemberToRemove(member)}
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card dir={isRTL ? 'rtl' : 'ltr'} style={{ textAlign: isRTL ? 'right' : 'left' }} className="border-border/90">
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
              <UserPlus className="h-5 w-5" />
              {t('users.pendingInvitations')}
            </CardTitle>
            <CardDescription>
              {t('users.pendingInvitationsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {invitations.map((invitation) => (
                <div 
                  key={invitation.invitationId} 
                  className={`flex items-center justify-between p-4 border rounded-lg border-dashed hover:bg-accent/50 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                >
                  <div className={`flex items-center gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-muted">
                        <Phone className="h-4 w-4" />
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium" dir="ltr">{formatPhoneNumber(invitation.phoneNumber)}</p>
                      <p className="text-sm text-muted-foreground">
                        {t('invite.invited')} {new Date(invitation.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <Badge variant="outline">{t('users.pending')}</Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelInvitation(invitation.invitationId)}
                      className="text-destructive hover:text-destructive"
                    >
                      {t('invite.cancel')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Remove Member Confirmation Dialog */}
      <AlertDialog open={!!memberToRemove} onOpenChange={(open) => !open && setMemberToRemove(null)}>
        <AlertDialogContent dir={isRTL ? 'rtl' : 'ltr'}>
          <AlertDialogHeader>
            <AlertDialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <AlertTriangle className="h-5 w-5 text-destructive" />
              {t('users.removeMemberTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('users.removeMemberConfirm', { 
                name: memberToRemove?.fullName || memberToRemove?.phoneNumber || t('users.unnamed') 
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <AlertDialogCancel disabled={removeLoading}>
              {t('invite.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              disabled={removeLoading}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeLoading ? (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent" />
              ) : (
                t('users.removeButton')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Success Dialog */}
      <Dialog open={inviteSuccessDialogOpen} onOpenChange={setInviteSuccessDialogOpen}>
        <DialogContent dir={isRTL ? 'rtl' : 'ltr'} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
              <Check className="h-5 w-5 text-green-500" />
              {t('invite.successTitle')}
            </DialogTitle>
            <DialogDescription>
              {t('invite.successDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {t('invite.noNotificationWarning')}
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label>{t('invite.shareInstructions')}</Label>
              <div 
                className={`flex items-center gap-2 p-3 bg-muted rounded-lg cursor-pointer hover:bg-muted/80 transition-colors ${isRTL ? 'flex-row-reverse' : ''}`}
                onClick={handleCopyLink}
                title={t('invite.copyLink')}
              >
                <ExternalLink className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <code className="flex-1 text-sm break-all" dir="ltr">{websiteUrl}</code>
                {copiedLink ? (
                  <Check className="h-4 w-4 text-green-500 flex-shrink-0" />
                ) : (
                  <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground flex-shrink-0" />
                )}
              </div>
              {copiedLink && (
                <p className="text-xs text-green-600">{t('invite.linkCopied')}</p>
              )}
            </div>
          </div>

          <DialogFooter className={`flex-wrap gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <Button
              onClick={handleShareWhatsApp}
              className={`flex items-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <MessageCircle className="h-4 w-4" />
              {t('invite.shareWhatsApp')}
            </Button>
            <Button
              variant="outline"
              onClick={handleShareLink}
              className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}
            >
              <Share2 className="h-4 w-4" />
              {t('invite.shareLink')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
